/**
 * Phase 5 WebGL board — A+B + Light C1 + seasonal map look + living chrome.
 * Raw WebGL terrain + 2D chrome overlay (routes/settlements/labels/army/deposits).
 * Sim frozen; view-only. Survives full root.innerHTML rebuilds via detach/reattach.
 */
import type { Season } from "./clock.ts";
import type { Biome, HexCell, HexMap, ResourceFlag } from "./map.ts";
import { BIOME_COLORS, cellKey, neighbors, parseKey } from "./map.ts";
import { isSpringRiverBlocked } from "./tileStats.ts";

/** Pointy-top axial → pixel (mirrors render.ts; kept local to avoid cycles). */
function axialToPixel(q: number, r: number, size: number): { x: number; y: number } {
  const x = size * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r);
  const y = size * ((3 / 2) * r);
  return { x, y };
}

export interface WebglBoardFrame {
  map: HexMap;
  hexSize: number;
  season: Season;
  selectedKey: string | null;
  claimed: ReadonlySet<string>;
  boundaryKeys: ReadonlySet<string>;
  armyAtKey: string | null;
  armyObjKey: string | null;
  /** When false, skip seasonal colour shift (settings seat). */
  seasonTint: boolean;
  /** Soft edge vignette. */
  edgeFade: boolean;
  reduceMotion: boolean;
  /** Light C1 pan/zoom/tilt (settings feature.mapCamera). */
  mapCamera: boolean;
  /** Contour/elev relief on mesh + chrome (settings feature.mapRelief). */
  mapRelief: boolean;
}

export interface WebglBoardHandle {
  canvas: HTMLCanvasElement;
  /** Transparent 2D chrome layer (routes, markers, labels) over GL terrain. */
  chrome: HTMLCanvasElement;
  glOk: boolean;
  mount(host: HTMLElement): void;
  /** Detach canvas before host.innerHTML wipe so GL context survives. */
  detach(): void;
  draw(frame: WebglBoardFrame): void;
  resize(): void;
  /** Map click → cell key, or null if miss / outside. */
  pick(clientX: number, clientY: number): string | null;
  /** Wire Light C1 pan/zoom/tilt + click-select. Safe to call each render. */
  bindInput(onSelect: (key: string) => void): void;
  /** Reset camera to fit whole map. */
  resetCamera(): void;
  destroy(): void;
}

const VERT = `#version 300 es
precision highp float;
layout(location=0) in vec2 a_pos;
layout(location=1) in vec3 a_color;
layout(location=2) in float a_elev;
uniform vec4 u_view; // minX, minY, maxX, maxY
uniform vec2 u_light; // light dir xy (normalized-ish)
uniform float u_ambient;
uniform float u_tilt; // foreshorten 0..TILT_MAX — never past edge-on
uniform float u_yaw;  // radians, horizontal orbit (clamped)
uniform vec2 u_origin; // world-space rotation origin (map center)
out vec3 v_color;
out float v_shade;
out float v_elev;
out vec2 v_uv;
void main() {
  // Yaw about map origin, then elev tilt (clamped host-side — no underside)
  float dx = a_pos.x - u_origin.x;
  float dy = a_pos.y - u_origin.y;
  float cs = cos(u_yaw);
  float sn = sin(u_yaw);
  float rx = dx * cs - dy * sn;
  float ry = dx * sn + dy * cs;
  float xw = u_origin.x + rx;
  float yw = u_origin.y + ry - u_tilt * a_elev * (u_view.w - u_view.y) * 0.055;
  float x = (xw - u_view.x) / (u_view.z - u_view.x) * 2.0 - 1.0;
  float y = 1.0 - (yw - u_view.y) / (u_view.w - u_view.y) * 2.0;
  gl_Position = vec4(x, y, 0.0, 1.0);
  // Contour/relief: elev drives shade + rim; taller mtn/hill ladder
  float slope = clamp(a_elev * 0.85 + 0.12, -0.7, 1.85);
  float ndl = clamp(0.34 + 0.66 * dot(normalize(vec2(slope * 0.55 + 0.08, 0.9)), u_light), 0.2, 1.38);
  v_shade = mix(u_ambient, 1.0, ndl);
  v_color = a_color;
  v_elev = a_elev;
  v_uv = vec2(x, y);
}
`;

const FRAG = `#version 300 es
precision highp float;
in vec3 v_color;
in float v_shade;
in float v_elev;
in vec2 v_uv;
uniform vec3 u_season; // rgb multiplicative tint
uniform float u_seasonAmt;
uniform float u_desat; // winter ~0.5 sat pull toward luma + cool bias
uniform float u_vignette;
uniform vec3 u_bg;
out vec4 outColor;
void main() {
  vec3 base = v_color * v_shade;
  // Contour read: cool low / warm high — scaled for taller mountain ladder
  base *= mix(vec3(0.86, 0.92, 1.1), vec3(1.16, 1.04, 0.86), clamp(v_elev * 0.42 + 0.32, 0.0, 1.0));
  base = mix(base, base * u_season, u_seasonAmt);
  // Playtest winter: −50% saturation + slight blue tint on all terrain colors
  float luma = dot(base, vec3(0.299, 0.587, 0.114));
  base = mix(base, vec3(luma), u_desat);
  base = mix(base, vec3(1.0), u_desat * 0.10);
  float r = length(v_uv);
  float vig = 1.0 - u_vignette * smoothstep(0.55, 1.35, r);
  base *= vig;
  base = mix(u_bg, base, 0.92);
  outColor = vec4(base, 1.0);
}
`;

const VERT1 = `
attribute vec2 a_pos;
attribute vec3 a_color;
attribute float a_elev;
uniform vec4 u_view;
uniform vec2 u_light;
uniform float u_ambient;
uniform float u_tilt;
uniform float u_yaw;
uniform vec2 u_origin;
varying vec3 v_color;
varying float v_shade;
varying float v_elev;
varying vec2 v_uv;
void main() {
  float dx = a_pos.x - u_origin.x;
  float dy = a_pos.y - u_origin.y;
  float cs = cos(u_yaw);
  float sn = sin(u_yaw);
  float rx = dx * cs - dy * sn;
  float ry = dx * sn + dy * cs;
  float xw = u_origin.x + rx;
  float yw = u_origin.y + ry - u_tilt * a_elev * (u_view.w - u_view.y) * 0.055;
  float x = (xw - u_view.x) / (u_view.z - u_view.x) * 2.0 - 1.0;
  float y = 1.0 - (yw - u_view.y) / (u_view.w - u_view.y) * 2.0;
  gl_Position = vec4(x, y, 0.0, 1.0);
  float slope = clamp(a_elev * 0.85 + 0.12, -0.7, 1.85);
  float ndl = clamp(0.34 + 0.66 * dot(normalize(vec2(slope * 0.55 + 0.08, 0.9)), u_light), 0.2, 1.38);
  v_shade = mix(u_ambient, 1.0, ndl);
  v_color = a_color;
  v_elev = a_elev;
  v_uv = vec2(x, y);
}
`;

const FRAG1 = `
precision mediump float;
varying vec3 v_color;
varying float v_shade;
varying float v_elev;
varying vec2 v_uv;
uniform vec3 u_season;
uniform float u_seasonAmt;
uniform float u_desat;
uniform float u_vignette;
uniform vec3 u_bg;
void main() {
  vec3 base = v_color * v_shade;
  base *= mix(vec3(0.86, 0.92, 1.1), vec3(1.16, 1.04, 0.86), clamp(v_elev * 0.42 + 0.32, 0.0, 1.0));
  base = mix(base, base * u_season, u_seasonAmt);
  float luma = dot(base, vec3(0.299, 0.587, 0.114));
  base = mix(base, vec3(luma), u_desat);
  base = mix(base, vec3(1.0), u_desat * 0.10);
  float r = length(v_uv);
  float vig = 1.0 - u_vignette * smoothstep(0.55, 1.35, r);
  base *= vig;
  base = mix(u_bg, base, 0.92);
  gl_FragColor = vec4(base, 1.0);
}
`;

/** Seasonal multiplicative tints (A+B map look). */
const SEASON_RGB: Record<Season, [number, number, number]> = {
  Spring: [0.82, 1.08, 0.88],
  Summer: [1.1, 1.05, 0.78],
  Autumn: [1.12, 0.82, 0.62],
  // Winter: ~−50% saturation + slight blue tint (playtest 0.5.5)
  Winter: [0.72, 0.82, 1.18],
};

/** Light C1 direction by season (sun path cue). */
const SEASON_LIGHT: Record<Season, [number, number]> = {
  Spring: [0.35, 0.9],
  Summer: [0.15, 1.0],
  Autumn: [0.55, 0.75],
  Winter: [0.7, 0.55],
};

/**
 * Terrain height ladder (draped-sheet relief) — amplitude tuned so tile types
 * read as real relief, not a flat sticker pack.
 * Mountains poke highest → foothills/hills → forest/plains/marsh/river lowlands → open water lowest.
 * Rivers sit with grasslands (centerline chrome carries the water read — not full-hex fill).
 */
const ELEV: Partial<Record<Biome, number>> = {
  water: -0.62,
  river: 0.02,
  marsh: -0.14,
  plains: 0.06,
  coast: 0.0,
  forest: 0.16,
  // +70% height range on relief tiers (playtest: map still too flat)
  foothills: 0.816,
  hills: 1.156,
  mountains: 1.785,
};

/**
 * Pointy-top corner i (hexCorners order) is shared by this hex + two neighbors.
 * Order matches angle 60*i-30: NE, SE, S, SW, NW, N.
 */
const CORNER_NEIGHBOR_DIRS: readonly (readonly (readonly [number, number])[])[] = [
  [
    [1, 0],
    [1, -1],
  ], // 0 NE — E + NE
  [
    [1, 0],
    [0, 1],
  ], // 1 SE — E + SE
  [
    [0, 1],
    [-1, 1],
  ], // 2 S — SE + SW
  [
    [-1, 1],
    [-1, 0],
  ], // 3 SW — SW + W
  [
    [-1, 0],
    [0, -1],
  ], // 4 NW — W + NW
  [
    [0, -1],
    [1, -1],
  ], // 5 N — NW + NE
];

function biomeElev(biome: Biome): number {
  return ELEV[biome] ?? 0;
}

/** River hexes keep sim biome but paint as lowland under the centerline. */
function paintBiomeOf(c: HexCell): Biome {
  return c.biome === "river" ? "plains" : c.biome;
}

function paintRgbForCell(c: HexCell): [number, number, number] {
  const [r, g, b] = hexToRgb(BIOME_COLORS[paintBiomeOf(c)]);
  if (c.biome !== "river") return [r, g, b];
  // Cool lowland bed under the river stroke (not a full blue hex fill)
  return [r * 0.72 + 0.08, g * 0.82 + 0.1, Math.min(1, b * 0.75 + 0.22)];
}

/** Settlement / pass micro-bumps on cell centers only (edges stay terrain-draped). */
function cellCenterElev(c: HexCell, reliefOn: boolean): number {
  if (!reliefOn) return 0;
  let elev = biomeElev(c.biome);
  if (c.settlement === "seat") elev += 0.28;
  else if (c.settlement === "bannerman") elev += 0.12;
  else if (c.settlement === "political") elev += 0.18;
  if (c.mountainPass) elev -= 0.1;
  return elev;
}

/**
 * Draped-sheet corner height: average this cell + the two hexes that share the vertex.
 * Missing neighbors (map edge) drop out of the average so rims ease out cleanly.
 */
function cornerElev(
  map: HexMap,
  q: number,
  r: number,
  cornerIdx: number,
  reliefOn: boolean,
  selfElev: number,
): number {
  if (!reliefOn) return selfElev;
  const dirs = CORNER_NEIGHBOR_DIRS[cornerIdx] ?? [];
  let sum = selfElev;
  let n = 1;
  for (const [dq, dr] of dirs) {
    const o = map.cells.get(cellKey(q + dq, r + dr));
    if (o) {
      sum += biomeElev(o.biome);
      n += 1;
    }
  }
  return sum / n;
}

/**
 * Shared-vertex biome colour blend — softens abrupt tile-edge colour seams.
 * Averages this cell paint RGB with the two hexes that share the corner.
 */
function cornerRgb(
  map: HexMap,
  q: number,
  r: number,
  cornerIdx: number,
  selfRgb: [number, number, number],
): [number, number, number] {
  const dirs = CORNER_NEIGHBOR_DIRS[cornerIdx] ?? [];
  let sr = selfRgb[0];
  let sg = selfRgb[1];
  let sb = selfRgb[2];
  let n = 1;
  for (const [dq, dr] of dirs) {
    const o = map.cells.get(cellKey(q + dq, r + dr));
    if (o) {
      const [or, og, ob] = paintRgbForCell(o);
      sr += or;
      sg += og;
      sb += ob;
      n += 1;
    }
  }
  return [sr / n, sg / n, sb / n];
}

/** Deterministic -1..1 hash from a short string (edge keys → river wander). */
function hashUnit(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // map to [-1, 1]
  return ((h >>> 0) % 2001) / 1000 - 1;
}

/**
 * Smooth cubic through A→B with shared endpoint tangents (seeded by vertex key).
 * Keeps river/road chains continuous across hex joints instead of kinked per-edge arcs.
 */
function pathCubicControls(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  seed: string,
  amount: number,
): { c1x: number; c1y: number; c2x: number; c2y: number } {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  // Vertex-stable side bias → neighboring edges share bend direction at joints
  const aKey = `${ax.toFixed(1)},${ay.toFixed(1)}`;
  const bKey = `${bx.toFixed(1)},${by.toFixed(1)}`;
  const sideA = hashUnit(aKey + "|side");
  const sideB = hashUnit(bKey + "|side");
  const lobe = hashUnit(seed + "|l") * 0.35;
  const along1 = hashUnit(seed + "|a1") * 0.22;
  const along2 = hashUnit(seed + "|a2") * 0.22;
  const mag1 = amount * (0.88 + Math.abs(sideA) * 0.42 + Math.abs(lobe) * 0.22);
  const mag2 = amount * (0.88 + Math.abs(sideB) * 0.42 + Math.abs(lobe) * 0.18);
  const s1 = sideA >= 0 ? 1 : -1;
  // Fillet: same-side wrap around the shared hex vertex — no S-flip through the corner.
  const t1 = 0.22 + along1 * 0.1;
  const t2 = 0.78 + along2 * 0.1;
  const fillet = amount * 0.36;
  return {
    c1x: ax + dx * t1 + px * (mag1 + fillet) * s1,
    c1y: ay + dy * t1 + py * (mag1 + fillet) * s1,
    c2x: ax + dx * t2 + px * (mag2 + fillet) * s1,
    c2y: ay + dy * t2 + py * (mag2 + fillet) * s1,
  };
}

/** Max elev foreshorten — never past edge-on / underside. */
const TILT_MAX = 0.11;
const TILT_MIN = 0;
/** Horizontal orbit clamp (radians). */
const YAW_MAX = Math.PI * 0.92;

const DEPOSIT_LABEL: Record<ResourceFlag, string> = {
  iron: "Fe",
  gold: "Au",
  horses: "Ho",
};

const SETTLE_FILL: Record<string, string> = {
  seat: "#e8c85a",
  bannerman: "#c9a227",
  liege: "#f0e0b8",
  peer: "#8ab0d0",
  foe: "#b84a3a",
  political: "#8ab0d0",
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function linkProg(
  gl: WebGLRenderingContext,
  vsSrc: string,
  fsSrc: string,
): WebGLProgram | null {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc);
  if (!vs || !fs) return null;
  const p = gl.createProgram();
  if (!p) return null;
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    gl.deleteProgram(p);
    return null;
  }
  return p;
}

function hexCorners(cx: number, cy: number, size: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = ((60 * i - 30) * Math.PI) / 180;
    pts.push({ x: cx + size * Math.cos(angle), y: cy + size * Math.sin(angle) });
  }
  return pts;
}

/** Pointy-top axial from pixel (inverse of axialToPixel). */
export function pixelToAxial(x: number, y: number, size: number): { q: number; r: number } {
  const q = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / size;
  const r = ((2 / 3) * y) / size;
  return cubeRound(q, r);
}

function cubeRound(q: number, r: number): { q: number; r: number } {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);
  const qDiff = Math.abs(rq - q);
  const rDiff = Math.abs(rr - r);
  const sDiff = Math.abs(rs - s);
  if (qDiff > rDiff && qDiff > sDiff) rq = -rr - rs;
  else if (rDiff > sDiff) rr = -rq - rs;
  return { q: rq, r: rr };
}

function prefersReducedMotion(): boolean {
  if (typeof matchMedia !== "function") return false;
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type Cam = { panX: number; panY: number; zoom: number; tilt: number; yaw: number };

function defaultCam(): Cam {
  return { panX: 0, panY: 0, zoom: 1, tilt: 0.06, yaw: 0 };
}

function clampTilt(t: number): number {
  return Math.max(TILT_MIN, Math.min(TILT_MAX, t));
}

function clampYaw(y: number): number {
  return Math.max(-YAW_MAX, Math.min(YAW_MAX, y));
}

export function createWebglBoard(): WebglBoardHandle {
  const canvas = document.createElement("canvas");
  canvas.className = "map map-webgl webgl-board";
  canvas.setAttribute("role", "img");
  canvas.setAttribute("aria-label", "Realm hex map");
  canvas.style.display = "block";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.touchAction = "none";

  const chrome = document.createElement("canvas");
  chrome.className = "map map-chrome webgl-chrome";
  chrome.setAttribute("aria-hidden", "true");
  chrome.style.display = "block";
  chrome.style.width = "100%";
  chrome.style.height = "100%";
  chrome.style.touchAction = "none";
  chrome.style.pointerEvents = "none";

  let gl: WebGL2RenderingContext | WebGLRenderingContext | null = null;
  let isWebgl2 = false;
  let ctx2d: CanvasRenderingContext2D | null = null;
  let chromeCtx: CanvasRenderingContext2D | null = null;
  try {
    const g2 = canvas.getContext("webgl2", {
      antialias: true,
      alpha: false,
      premultipliedAlpha: false,
      powerPreference: "default",
    }) as WebGL2RenderingContext | null;
    if (g2) {
      gl = g2;
      isWebgl2 = true;
    } else {
      gl = canvas.getContext("webgl", {
        antialias: true,
        alpha: false,
        premultipliedAlpha: false,
      }) as WebGLRenderingContext | null;
      isWebgl2 = false;
    }
  } catch {
    gl = null;
  }

  let prog: WebGLProgram | null = null;
  let buf: WebGLBuffer | null = null;
  let vao: WebGLVertexArrayObject | null = null;
  const loc = {
    view: null as WebGLUniformLocation | null,
    light: null as WebGLUniformLocation | null,
    ambient: null as WebGLUniformLocation | null,
    season: null as WebGLUniformLocation | null,
    seasonAmt: null as WebGLUniformLocation | null,
    desat: null as WebGLUniformLocation | null,
    vignette: null as WebGLUniformLocation | null,
    bg: null as WebGLUniformLocation | null,
    tilt: null as WebGLUniformLocation | null,
    yaw: null as WebGLUniformLocation | null,
    origin: null as WebGLUniformLocation | null,
  };
  /** World AABB of hex content (no camera). */
  let worldBox = { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  /** Effective view AABB after camera (what shaders/pick use). */
  let viewBox = { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  let cam: Cam = defaultCam();
  let lastFrame: WebglBoardFrame | null = null;
  let vertCount = 0;
  let destroyed = false;
  let ro: ResizeObserver | null = null;
  let inputBound = false;
  let onSelectCb: ((key: string) => void) | null = null;
  let drag: {
    active: boolean;
    moved: boolean;
    /** pan = LMB drag; orbit = RMB / Shift+LMB (yaw + mild tilt) */
    mode: "pan" | "orbit";
    startX: number;
    startY: number;
    origPanX: number;
    origPanY: number;
    origYaw: number;
    origTilt: number;
    pointerId: number;
  } | null = null;

  if (gl) {
    prog = isWebgl2 ? linkProg(gl, VERT, FRAG) : linkProg(gl, VERT1, FRAG1);
    if (!prog) {
      // GL init failed after claiming the canvas context — need a fresh element for 2D.
      gl = null;
      const replacement = document.createElement("canvas");
      replacement.className = canvas.className;
      replacement.setAttribute("role", "img");
      replacement.setAttribute("aria-label", "Realm hex map");
      replacement.style.cssText = canvas.style.cssText;
      ctx2d = replacement.getContext("2d");
      Object.defineProperty(canvas, "_fallbackEl", { value: replacement, configurable: true });
    } else {
      buf = gl.createBuffer();
      loc.view = gl.getUniformLocation(prog, "u_view");
      loc.light = gl.getUniformLocation(prog, "u_light");
      loc.ambient = gl.getUniformLocation(prog, "u_ambient");
      loc.season = gl.getUniformLocation(prog, "u_season");
      loc.seasonAmt = gl.getUniformLocation(prog, "u_seasonAmt");
      loc.desat = gl.getUniformLocation(prog, "u_desat");
      loc.vignette = gl.getUniformLocation(prog, "u_vignette");
      loc.bg = gl.getUniformLocation(prog, "u_bg");
      loc.tilt = gl.getUniformLocation(prog, "u_tilt");
      loc.yaw = gl.getUniformLocation(prog, "u_yaw");
      loc.origin = gl.getUniformLocation(prog, "u_origin");

      if (isWebgl2) {
        const g2 = gl as WebGL2RenderingContext;
        vao = g2.createVertexArray();
        g2.bindVertexArray(vao);
        g2.bindBuffer(g2.ARRAY_BUFFER, buf);
        const stride = 6 * 4;
        g2.enableVertexAttribArray(0);
        g2.vertexAttribPointer(0, 2, g2.FLOAT, false, stride, 0);
        g2.enableVertexAttribArray(1);
        g2.vertexAttribPointer(1, 3, g2.FLOAT, false, stride, 8);
        g2.enableVertexAttribArray(2);
        g2.vertexAttribPointer(2, 1, g2.FLOAT, false, stride, 20);
        g2.bindVertexArray(null);
      }
    }
  } else {
    ctx2d = canvas.getContext("2d");
  }

  chromeCtx = chrome.getContext("2d");

  const glOk = !!(gl && prog);
  /** Active drawing surface (GL canvas or 2D fallback canvas). */
  const surface = (): HTMLCanvasElement => {
    const fb = (canvas as HTMLCanvasElement & { _fallbackEl?: HTMLCanvasElement })._fallbackEl;
    return fb ?? canvas;
  };

  const liveGl = (): WebGLRenderingContext | WebGL2RenderingContext | null =>
    destroyed ? null : gl;

  function computeWorldBox(frame: WebglBoardFrame): void {
    const { map, hexSize } = frame;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const c of map.cells.values()) {
      const { x, y } = axialToPixel(c.q, c.r, hexSize);
      minX = Math.min(minX, x - hexSize);
      minY = Math.min(minY, y - hexSize);
      maxX = Math.max(maxX, x + hexSize);
      maxY = Math.max(maxY, y + hexSize);
    }
    const pad = hexSize;
    worldBox = { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
  }

  /** Apply camera to worldBox → viewBox (zoom about center + pan in world units). */
  function applyCamera(): void {
    const w = worldBox.maxX - worldBox.minX;
    const h = worldBox.maxY - worldBox.minY;
    const z = Math.max(0.55, Math.min(2.8, cam.zoom));
    cam.zoom = z;
    cam.tilt = clampTilt(cam.tilt);
    cam.yaw = clampYaw(cam.yaw);
    const hw = w / z / 2;
    const hh = h / z / 2;
    // Soft clamp pan so map never fully leaves frame
    const maxPanX = w * 0.45;
    const maxPanY = h * 0.45;
    cam.panX = Math.max(-maxPanX, Math.min(maxPanX, cam.panX));
    cam.panY = Math.max(-maxPanY, Math.min(maxPanY, cam.panY));
    const cx2 = (worldBox.minX + worldBox.maxX) / 2 + cam.panX;
    const cy2 = (worldBox.minY + worldBox.maxY) / 2 + cam.panY;
    viewBox = {
      minX: cx2 - hw,
      minY: cy2 - hh,
      maxX: cx2 + hw,
      maxY: cy2 + hh,
    };
  }

  /** Orbit pivot: selected tile, else seat, else world-box center. */
  function mapOrigin(): { ox: number; oy: number } {
    const hexSize = lastFrame?.hexSize ?? 1;
    const selectedKey = lastFrame?.selectedKey;
    if (selectedKey) {
      const { q, r } = parseKey(selectedKey);
      const p = axialToPixel(q, r, hexSize);
      return { ox: p.x, oy: p.y };
    }
    const seatKey = lastFrame?.map.seatKey;
    if (seatKey) {
      const { q, r } = parseKey(seatKey);
      const p = axialToPixel(q, r, hexSize);
      return { ox: p.x, oy: p.y };
    }
    return {
      ox: (worldBox.minX + worldBox.maxX) / 2,
      oy: (worldBox.minY + worldBox.maxY) / 2,
    };
  }

  function ensureSize(): void {
    const el = surface();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const host = el.parentElement;
    const cw = el.clientWidth || host?.clientWidth || 0;
    const ch = el.clientHeight || host?.clientHeight || 0;
    const w = Math.max(1, Math.floor(cw * dpr));
    const h = Math.max(1, Math.floor(ch * dpr));
    if (el.width !== w || el.height !== h) {
      el.width = w;
      el.height = h;
    }
    if (chrome.width !== w || chrome.height !== h) {
      chrome.width = w;
      chrome.height = h;
    }
    const g = liveGl();
    if (g) g.viewport(0, 0, el.width, el.height);
  }

  function worldToScreen(
    x: number,
    y: number,
    elev: number,
    w: number,
    h: number,
    tiltAmt: number,
    yawAmt = 0,
  ): { sx: number; sy: number } {
    const { ox, oy } = mapOrigin();
    const dx = x - ox;
    const dy = y - oy;
    const cs = Math.cos(yawAmt);
    const sn = Math.sin(yawAmt);
    const rx = dx * cs - dy * sn;
    const ry = dx * sn + dy * cs;
    const xw = ox + rx;
    const yw = oy + ry - tiltAmt * elev * (viewBox.maxY - viewBox.minY) * 0.055;
    const sx = ((xw - viewBox.minX) / (viewBox.maxX - viewBox.minX)) * w;
    const sy = ((yw - viewBox.minY) / (viewBox.maxY - viewBox.minY)) * h;
    return { sx, sy };
  }

  function paintChrome(frame: WebglBoardFrame, combined2d: boolean): void {
    const el = combined2d ? surface() : chrome;
    const ctx = combined2d
      ? (ctx2d ?? el.getContext("2d"))
      : (chromeCtx ?? chrome.getContext("2d"));
    if (!ctx) return;
    if (combined2d) ctx2d = ctx;
    else chromeCtx = ctx;

    const w = el.width;
    const h = el.height;
    if (!combined2d) {
      ctx.clearRect(0, 0, w, h);
    }

    const {
      map,
      hexSize,
      season,
      selectedKey,
      claimed,
      boundaryKeys,
      armyAtKey,
      armyObjKey,
      seasonTint,
      reduceMotion,
      mapRelief,
    } = frame;
    const reduce = reduceMotion || prefersReducedMotion();
    const reliefOn = mapRelief !== false;
    const tiltAmt = reduce || !frame.mapCamera ? 0 : cam.tilt;
    const yawAmt = reduce || !frame.mapCamera ? 0 : cam.yaw;
    const tint = seasonTint ? SEASON_RGB[season] : ([1, 1, 1] as [number, number, number]);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scale = w / Math.max(1e-6, viewBox.maxX - viewBox.minX);
    const lineW = Math.max(1.2 * dpr, hexSize * 0.09 * scale);

    // —— Routes (roads): stronger meander + smooth cubic bends ——
    ctx.save();
    ctx.strokeStyle = "rgba(72, 44, 22, 0.275)";
    ctx.lineWidth = lineW;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const roadWander = hexSize * 0.72;
    const drawn = new Set<string>();
    for (const c of map.cells.values()) {
      const a = axialToPixel(c.q, c.r, hexSize);
      for (const rk of c.routes) {
        const edge = [cellKey(c.q, c.r), rk].sort().join("|");
        if (drawn.has(edge)) continue;
        drawn.add(edge);
        const other = map.cells.get(rk);
        if (!other) continue;
        const b = axialToPixel(other.q, other.r, hexSize);
        const ea = cellCenterElev(c, reliefOn);
        const eb = cellCenterElev(other, reliefOn);
        const { c1x, c1y, c2x, c2y } = pathCubicControls(
          a.x,
          a.y,
          b.x,
          b.y,
          edge + "|rd",
          roadWander,
        );
        const e1 = ea * 0.67 + eb * 0.33;
        const e2 = ea * 0.33 + eb * 0.67;
        const pa = worldToScreen(a.x, a.y, ea, w, h, tiltAmt, yawAmt);
        const p1 = worldToScreen(c1x, c1y, e1, w, h, tiltAmt, yawAmt);
        const p2 = worldToScreen(c2x, c2y, e2, w, h, tiltAmt, yawAmt);
        const pb = worldToScreen(b.x, b.y, eb, w, h, tiltAmt, yawAmt);
        ctx.beginPath();
        ctx.moveTo(pa.sx, pa.sy);
        ctx.bezierCurveTo(p1.sx, p1.sy, p2.sx, p2.sy, pb.sx, pb.sy);
        ctx.stroke();
      }
    }
    ctx.restore();

    // —— Rivers: smooth cubics + season flood/ice treatment ——
    // Connect river centers to adjacent river/water; terminate at water edge.
    {
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.globalCompositeOperation = "source-over";
      const floodMul = season === "Spring" ? 1.5 : 1;
      const riverW = Math.max(1.8 * dpr, hexSize * 0.18 * scale) * floodMul;
      const wander = hexSize * 0.82;
      const drawnRiv = new Set<string>();
      // Winter: desaturated icy blue + soft edge blur; Spring: flood blue
      const waterFill =
        season === "Winter"
          ? "rgba(168, 188, 231, 0.5)"
          : season === "Spring"
            ? "rgba(85, 155, 205, 0.92)"
            : "rgba(65, 135, 190, 0.9)";
      if (season === "Winter" && !reduce) {
        ctx.shadowColor = "rgba(190, 215, 235, 0.55)";
        ctx.shadowBlur = Math.max(3.5 * dpr, riverW * 0.85);
      }
      for (const c of map.cells.values()) {
        if (c.biome !== "river") continue;
        const a = axialToPixel(c.q, c.r, hexSize);
        const ea = cellCenterElev(c, reliefOn);
        for (const [nq, nr] of neighbors(c.q, c.r)) {
          const o = map.cells.get(cellKey(nq, nr));
          if (!o) continue;
          if (o.biome !== "river" && o.biome !== "water") continue;
          const edge = [cellKey(c.q, c.r), cellKey(nq, nr)].sort().join("|");
          if (drawnRiv.has(edge)) continue;
          drawnRiv.add(edge);
          const b = axialToPixel(o.q, o.r, hexSize);
          // Water terminals use water elev so the stroke drapes into the basin
          const eb =
            o.biome === "water"
              ? reliefOn
                ? biomeElev("water")
                : 0
              : cellCenterElev(o, reliefOn);
          const { c1x, c1y, c2x, c2y } = pathCubicControls(a.x, a.y, b.x, b.y, edge, wander);
          const e1 = ea * 0.67 + eb * 0.33;
          const e2 = ea * 0.33 + eb * 0.67;
          const pa = worldToScreen(a.x, a.y, ea, w, h, tiltAmt, yawAmt);
          const p1 = worldToScreen(c1x, c1y, e1, w, h, tiltAmt, yawAmt);
          const p2 = worldToScreen(c2x, c2y, e2, w, h, tiltAmt, yawAmt);
          const pb = worldToScreen(b.x, b.y, eb, w, h, tiltAmt, yawAmt);
          // Width varies ±15% per edge (deterministic); spring already scaled
          const widthMul = 1 + 0.15 * hashUnit(edge + "|w");
          ctx.beginPath();
          ctx.strokeStyle = waterFill;
          ctx.lineWidth = riverW * widthMul;
          ctx.moveTo(pa.sx, pa.sy);
          ctx.bezierCurveTo(p1.sx, p1.sy, p2.sx, p2.sy, pb.sx, pb.sy);
          ctx.stroke();
        }
      }
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    // —— Per-cell chrome ——
    for (const c of map.cells.values()) {
      const { x, y } = axialToPixel(c.q, c.r, hexSize);
      const key = cellKey(c.q, c.r);
      const elev = cellCenterElev(c, reliefOn);
      const { sx, sy } = worldToScreen(x, y, elev, w, h, tiltAmt, yawAmt);
      const rMark = hexSize * 0.28 * scale;
      const rArmy = hexSize * 0.22 * scale;
      const rObj = hexSize * 0.3 * scale;
      const rClaim = hexSize * 0.12 * scale;
      const fontPx = Math.max(9 * dpr, hexSize * 0.22 * scale);

      // Claimed demesne dot
      if (claimed.has(key) && c.settlement === "none") {
        ctx.beginPath();
        ctx.arc(sx, sy + hexSize * 0.32 * scale, rClaim, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(232, 200, 90, 0.75)";
        ctx.fill();
      }

      // Boundary ring hint
      if (boundaryKeys.has(key)) {
        const corners = hexCorners(x, y, hexSize * 0.92);
        ctx.beginPath();
        corners.forEach((p, i) => {
          const pt = worldToScreen(p.x, p.y, elev, w, h, tiltAmt, yawAmt);
          if (i === 0) ctx.moveTo(pt.sx, pt.sy);
          else ctx.lineTo(pt.sx, pt.sy);
        });
        ctx.closePath();
        ctx.strokeStyle = "rgba(232, 200, 90, 0.55)";
        ctx.lineWidth = Math.max(1, 1.4 * dpr);
        ctx.stroke();
      }

      // Selected ring — primary visible hex edge (default plate seams stay near-invisible)
      if (key === selectedKey) {
        const corners = hexCorners(x, y, hexSize * 0.98);
        ctx.beginPath();
        corners.forEach((p, i) => {
          const pt = worldToScreen(p.x, p.y, elev + 0.1, w, h, tiltAmt, yawAmt);
          if (i === 0) ctx.moveTo(pt.sx, pt.sy);
          else ctx.lineTo(pt.sx, pt.sy);
        });
        ctx.closePath();
        ctx.strokeStyle = "rgba(255, 220, 120, 0.9)";
        ctx.lineWidth = Math.max(1.5, 2 * dpr);
        ctx.stroke();
      }

      // Resource deposit
      if (c.resourceFlag) {
        const glyph = DEPOSIT_LABEL[c.resourceFlag];
        ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const dx = sx + hexSize * 0.4 * scale;
        const dy = sy - hexSize * 0.28 * scale;
        ctx.fillStyle = "rgba(10, 16, 24, 0.55)";
        ctx.fillRect(dx - fontPx * 0.7, dy - fontPx * 0.55, fontPx * 1.4, fontPx * 1.1);
        ctx.fillStyle =
          c.resourceFlag === "gold"
            ? "#e8c85a"
            : c.resourceFlag === "iron"
              ? "#b0c0d0"
              : "#c4a882";
        ctx.fillText(glyph, dx, dy);
      }

      // Wood cue on forest / hills
      if (c.biome === "forest" || c.biome === "hills" || c.biome === "foothills") {
        ctx.font = `500 ${Math.max(8 * dpr, fontPx * 0.75)}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "rgba(160, 200, 140, 0.7)";
        ctx.fillText("+W", sx - hexSize * 0.38 * scale, sy - hexSize * 0.32 * scale);
      }

      // Mountain pass
      if (c.mountainPass) {
        ctx.font = `${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "rgba(200, 210, 230, 0.85)";
        ctx.fillText("⤒", sx, sy + hexSize * 0.5 * scale);
      }

      // Settlement marker + label
      if (c.settlement !== "none") {
        let fill = SETTLE_FILL.political!;
        if (c.settlement === "seat") fill = SETTLE_FILL.seat!;
        else if (c.settlement === "bannerman") fill = SETTLE_FILL.bannerman!;
        else if (c.seatRole === "liege") fill = SETTLE_FILL.liege!;
        else if (c.seatRole === "foe") fill = SETTLE_FILL.foe!;
        else if (c.seatRole === "peer") fill = SETTLE_FILL.peer!;

        ctx.beginPath();
        ctx.arc(sx, sy, rMark, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = Math.max(1, 1.2 * dpr);
        ctx.stroke();

        // Role icon
        if (c.settlement === "political" && c.seatRole) {
          const icon =
            c.seatRole === "liege" ? "♛" : c.seatRole === "peer" ? "♦" : "▲";
          ctx.font = `${fontPx * 0.95}px ui-sans-serif, system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "rgba(20, 16, 10, 0.9)";
          ctx.fillText(icon, sx, sy - hexSize * 0.42 * scale);
        }

        const label =
          c.settlement === "seat"
            ? "Seat"
            : c.settlement === "bannerman"
              ? (c.name?.slice(0, 4) ?? "Ban")
              : ((c.name ?? c.seatRole ?? "Keep").slice(0, 4));
        ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineWidth = Math.max(2, 3 * dpr);
        ctx.strokeStyle = "rgba(8, 12, 18, 0.75)";
        ctx.strokeText(label, sx, sy + 1);
        ctx.fillStyle = "#f4efdf";
        ctx.fillText(label, sx, sy + 1);
      }

      // Army / quest objective
      if (armyAtKey === key) {
        ctx.beginPath();
        ctx.arc(sx, sy, rArmy, 0, Math.PI * 2);
        ctx.fillStyle = "#8b1e1e";
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.stroke();
        ctx.font = `${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#f0e0b8";
        ctx.fillText("⚔", sx, sy + fontPx * 0.05);
      } else if (armyObjKey === key) {
        ctx.beginPath();
        ctx.arc(sx, sy, rObj, 0, Math.PI * 2);
        ctx.strokeStyle = "#e8c85a";
        ctx.lineWidth = Math.max(1.5, 2.2 * dpr);
        ctx.stroke();
      }

      // Spring flood wash cue on chrome only when 2d-combined already tinted fills
      if (combined2d && isSpringRiverBlocked(c.biome, season, false)) {
        /* fills handled in paint2d path */
      }
      void tint;
    }
  }

  function paint2d(frame: WebglBoardFrame): void {
    const el = surface();
    const ctx = ctx2d ?? el.getContext("2d");
    if (!ctx) return;
    ctx2d = ctx;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = el.clientWidth || el.parentElement?.clientWidth || 1;
    const ch = el.clientHeight || el.parentElement?.clientHeight || 1;
    const w = Math.max(1, Math.floor(cw * dpr));
    const h = Math.max(1, Math.floor(ch * dpr));
    el.width = w;
    el.height = h;
    chrome.width = w;
    chrome.height = h;

    computeWorldBox(frame);
    applyCamera();

    const {
      map,
      hexSize,
      season,
      selectedKey,
      claimed,
      boundaryKeys,
      seasonTint,
      reduceMotion,
      mapCamera,
      mapRelief,
    } = frame;
    const reduce = reduceMotion || prefersReducedMotion();
    const reliefOn = mapRelief !== false;
    const tiltAmt = reduce || !mapCamera ? 0 : cam.tilt;
    const yawAmt = reduce || !mapCamera ? 0 : cam.yaw;
    ctx.fillStyle = "#0a1018";
    ctx.fillRect(0, 0, w, h);
    const tint = seasonTint ? SEASON_RGB[season] : ([1, 1, 1] as [number, number, number]);
    // Winter desat on tiles only (2D fallback) — not a full-scene host filter
    const winterDesat = seasonTint && season === "Winter" ? (reduce ? 0.38 : 0.5) : 0;

    for (const c of map.cells.values()) {
      const { x, y } = axialToPixel(c.q, c.r, hexSize);
      const key = cellKey(c.q, c.r);
      let [r, g, b] = paintRgbForCell(c);
      let elev = cellCenterElev(c, reliefOn);
      r = Math.min(1, r * tint[0]);
      g = Math.min(1, g * tint[1]);
      b = Math.min(1, b * tint[2]);
      if (winterDesat > 0) {
        const luma = r * 0.299 + g * 0.587 + b * 0.114;
        r = r + (luma - r) * winterDesat;
        g = g + (luma - g) * winterDesat;
        b = b + (luma - b) * winterDesat;
        r = r + (1 - r) * 0.10;
        g = g + (1 - g) * 0.10;
        b = b + (1 - b) * 0.10;
      }
      if (isSpringRiverBlocked(c.biome, season, false)) {
        r = r * 0.7 + 0.15;
        g = g * 0.85 + 0.1;
        b = Math.min(1, b * 1.1 + 0.15);
        elev -= 0.05;
      }
      if (key === selectedKey) {
        r = Math.min(1, r * 0.7 + 0.35);
        g = Math.min(1, g * 0.7 + 0.28);
        b = Math.min(1, b * 0.5 + 0.05);
        elev += 0.12;
      } else if (boundaryKeys.has(key)) {
        r = Math.min(1, r * 0.85 + 0.2);
        g = Math.min(1, g * 0.85 + 0.15);
      }
      if (claimed.has(key) && c.settlement === "none") {
        r = Math.min(1, r * 0.92 + 0.08);
        g = Math.min(1, g * 0.95 + 0.05);
      }

      const corners = hexCorners(x, y, hexSize * 0.95);
      // Draped corners: each vertex shares height + colour with neighboring hexes
      const elevs = corners.map((_, i) =>
        cornerElev(map, c.q, c.r, i, reliefOn, biomeElev(c.biome)),
      );
      const selfRgb: [number, number, number] = [r, g, b];
      // Near-invisible plate seams — selection/boundary chrome carries the edge read
      const meanE = elevs.reduce((s, e) => s + e, 0) / 6;
      const edgeA = (0.012 + Math.max(0, meanE) * 0.02) * 0.4;
      // Gradient-free fan fill via per-corner average is expensive in 2D; paint
      // solid fill then a light rim. WebGL path does true corner RGB blend.
      ctx.beginPath();
      corners.forEach((p, i) => {
        const pt = worldToScreen(p.x, p.y, elevs[i] ?? elev, w, h, tiltAmt, yawAmt);
        if (i === 0) ctx.moveTo(pt.sx, pt.sy);
        else ctx.lineTo(pt.sx, pt.sy);
      });
      ctx.closePath();
      // Mild center→edge soft by mixing corner-mean colour into fill
      const cornerMean = corners.reduce(
        (acc, _, i) => {
          const [cr, cg, cb] = cornerRgb(map, c.q, c.r, i, selfRgb);
          acc[0] += cr;
          acc[1] += cg;
          acc[2] += cb;
          return acc;
        },
        [0, 0, 0] as [number, number, number],
      );
      const fr = selfRgb[0] * 0.55 + (cornerMean[0] / 6) * 0.45;
      const fg = selfRgb[1] * 0.55 + (cornerMean[1] / 6) * 0.45;
      const fb = selfRgb[2] * 0.55 + (cornerMean[2] / 6) * 0.45;
      ctx.fillStyle = `rgb(${(fr * 255) | 0},${(fg * 255) | 0},${(fb * 255) | 0})`;
      ctx.fill();
      // Lighter outline (cool plate seam, not near-black)
      ctx.strokeStyle = `rgba(118, 138, 162,${edgeA})`;
      ctx.lineWidth = Math.max(0.35, (0.28 + Math.max(0, meanE) * 0.22) * dpr);
      ctx.stroke();
    }

    paintChrome(frame, true);
  }

  function buildMesh(frame: WebglBoardFrame): Float32Array {
    const { map, hexSize, season, selectedKey, claimed, boundaryKeys, mapRelief } = frame;
    const reliefOn = mapRelief !== false;
    computeWorldBox(frame);
    applyCamera();

    const n = map.cells.size;
    // center fan 6 tris = 18 verts
    const out = new Float32Array(n * 18 * 6);
    let o = 0;

    const push = (x: number, y: number, r: number, g: number, b: number, elev: number) => {
      out[o++] = x;
      out[o++] = y;
      out[o++] = r;
      out[o++] = g;
      out[o++] = b;
      out[o++] = elev;
    };

    for (const c of map.cells.values()) {
      const { x, y } = axialToPixel(c.q, c.r, hexSize);
      const key = cellKey(c.q, c.r);
      let [r, g, b] = paintRgbForCell(c);
      let elev = cellCenterElev(c, reliefOn);

      if (isSpringRiverBlocked(c.biome, season, false)) {
        r = r * 0.65 + 0.12;
        g = g * 0.8 + 0.12;
        b = Math.min(1, b * 1.15 + 0.18);
        if (reliefOn) elev -= 0.05;
      }
      if (key === selectedKey) {
        r = Math.min(1, r * 0.55 + 0.45);
        g = Math.min(1, g * 0.55 + 0.32);
        b = Math.min(1, b * 0.4 + 0.08);
        if (reliefOn) elev += 0.15;
      } else if (boundaryKeys.has(key)) {
        r = Math.min(1, r * 0.8 + 0.25);
        g = Math.min(1, g * 0.85 + 0.18);
        b = Math.min(1, b * 0.85 + 0.05);
      }
      if (claimed.has(key) && c.settlement === "none") {
        r = Math.min(1, r * 0.9 + 0.12);
        g = Math.min(1, g * 0.92 + 0.1);
      }

      const corners = hexCorners(x, y, hexSize * 0.98);
      // Draped sheet: corner elev + RGB = mean(self + two vertex-sharing neighbors)
      // Center keeps cell elev (+ ridge cue); shared verts kill plate/colour seams.
      const selfRgb: [number, number, number] = [r, g, b];
      for (let i = 0; i < 6; i++) {
        const a = corners[i]!;
        const bpt = corners[(i + 1) % 6]!;
        const ea = cornerElev(map, c.q, c.r, i, reliefOn, biomeElev(c.biome));
        const eb = cornerElev(map, c.q, c.r, (i + 1) % 6, reliefOn, biomeElev(c.biome));
        const [ra, ga, ba] = cornerRgb(map, c.q, c.r, i, selfRgb);
        const [rb, gb, bb] = cornerRgb(map, c.q, c.r, (i + 1) % 6, selfRgb);
        const cr = Math.min(1, r * 1.04 + 0.015);
        const cg = Math.min(1, g * 1.03 + 0.015);
        const cb = Math.min(1, b * 1.02);
        // Mild edge shade from slope (not a hard plate outline)
        const slopeShade = reliefOn ? 0.92 + Math.min(0.1, Math.abs(ea - eb) * 0.2) : 1;
        const er = Math.min(1, ra * slopeShade);
        const eg = Math.min(1, ga * slopeShade);
        const eblue = Math.min(1, ba * slopeShade);
        const er2 = Math.min(1, rb * slopeShade);
        const eg2 = Math.min(1, gb * slopeShade);
        const eb2 = Math.min(1, bb * slopeShade);
        const eCenter = reliefOn ? elev + 0.05 : elev;
        push(x, y, cr, cg, cb, eCenter);
        push(a.x, a.y, er, eg, eblue, ea);
        push(bpt.x, bpt.y, er2, eg2, eb2, eb);
      }
    }
    vertCount = o / 6;
    return out.subarray(0, o);
  }

  function draw(frame: WebglBoardFrame): void {
    if (destroyed) return;
    lastFrame = frame;
    const g = liveGl();
    const useGl = !!(g && prog && buf);
    if (!useGl) {
      paint2d(frame);
      // keep chrome clear when combined into surface
      if (chromeCtx) {
        chromeCtx.clearRect(0, 0, chrome.width, chrome.height);
      }
      return;
    }
    ensureSize();
    const mesh = buildMesh(frame);
    g.bindBuffer(g.ARRAY_BUFFER, buf);
    g.bufferData(g.ARRAY_BUFFER, mesh, g.DYNAMIC_DRAW);

    const el = surface();
    g.useProgram(prog);
    g.viewport(0, 0, el.width, el.height);
    g.clearColor(0.039, 0.063, 0.094, 1);
    g.clear(g.COLOR_BUFFER_BIT);
    g.disable(g.DEPTH_TEST);
    g.disable(g.BLEND);

    const [lx, ly] = SEASON_LIGHT[frame.season];
    const [sr, sg, sb] = SEASON_RGB[frame.season];
    const reduce = frame.reduceMotion || prefersReducedMotion();
    const tiltAmt = reduce || frame.mapCamera === false ? 0 : cam.tilt;
    const yawAmt = reduce || frame.mapCamera === false ? 0 : cam.yaw;
    const { ox, oy } = mapOrigin();
    g.uniform4f(loc.view, viewBox.minX, viewBox.minY, viewBox.maxX, viewBox.maxY);
    g.uniform2f(loc.light, lx, ly);
    g.uniform1f(loc.ambient, reduce ? 0.72 : 0.48);
    g.uniform3f(loc.season, sr, sg, sb);
    g.uniform1f(loc.seasonAmt, frame.seasonTint ? (reduce ? 0.62 : 0.82) : 0.0);
    // Winter desat on terrain tiles only (FS); chrome rivers get ice colour separately
    const desatAmt =
      frame.seasonTint && frame.season === "Winter" ? (reduce ? 0.38 : 0.5) : 0.0;
    if (loc.desat) g.uniform1f(loc.desat, desatAmt);
    g.uniform1f(loc.vignette, frame.edgeFade ? (reduce ? 0.25 : 0.4) : 0.0);
    g.uniform3f(loc.bg, 0.039, 0.063, 0.094);
    if (loc.tilt) g.uniform1f(loc.tilt, tiltAmt);
    if (loc.yaw) g.uniform1f(loc.yaw, yawAmt);
    if (loc.origin) g.uniform2f(loc.origin, ox, oy);

    if (isWebgl2 && vao) {
      const g2 = g as WebGL2RenderingContext;
      g2.bindVertexArray(vao);
      g2.drawArrays(g2.TRIANGLES, 0, vertCount);
      g2.bindVertexArray(null);
    } else {
      const stride = 6 * 4;
      const aPos = g.getAttribLocation(prog!, "a_pos");
      const aCol = g.getAttribLocation(prog!, "a_color");
      const aElev = g.getAttribLocation(prog!, "a_elev");
      g.bindBuffer(g.ARRAY_BUFFER, buf);
      if (aPos >= 0) {
        g.enableVertexAttribArray(aPos);
        g.vertexAttribPointer(aPos, 2, g.FLOAT, false, stride, 0);
      }
      if (aCol >= 0) {
        g.enableVertexAttribArray(aCol);
        g.vertexAttribPointer(aCol, 3, g.FLOAT, false, stride, 8);
      }
      if (aElev >= 0) {
        g.enableVertexAttribArray(aElev);
        g.vertexAttribPointer(aElev, 1, g.FLOAT, false, stride, 20);
      }
      g.drawArrays(g.TRIANGLES, 0, vertCount);
    }

    paintChrome(frame, false);
  }

  function mount(host: HTMLElement): void {
    if (destroyed) return;
    const el = surface();
    if (el.parentElement !== host) {
      host.appendChild(el);
    }
    if (chrome.parentElement !== host) {
      host.appendChild(chrome);
    }
    if (!ro && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (lastFrame) draw(lastFrame);
        else resize();
      });
      ro.observe(host);
    }
    ensureSize();
    if (lastFrame) draw(lastFrame);
  }

  function detach(): void {
    const el = surface();
    if (el.parentElement) el.parentElement.removeChild(el);
    if (chrome.parentElement) chrome.parentElement.removeChild(chrome);
  }

  function resize(): void {
    ensureSize();
    if (lastFrame) draw(lastFrame);
  }

  function pick(clientX: number, clientY: number): string | null {
    if (!lastFrame) return null;
    const rect = surface().getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    if (nx < 0 || ny < 0 || nx > 1 || ny > 1) return null;
    // Screen → viewBox, then inverse yaw about map origin (tilt neglected at Light C1)
    let x = viewBox.minX + nx * (viewBox.maxX - viewBox.minX);
    let y = viewBox.minY + ny * (viewBox.maxY - viewBox.minY);
    const reduce = lastFrame.reduceMotion || prefersReducedMotion();
    const yawAmt = reduce || lastFrame.mapCamera === false ? 0 : cam.yaw;
    if (yawAmt !== 0) {
      const { ox, oy } = mapOrigin();
      const dx = x - ox;
      const dy = y - oy;
      const cs = Math.cos(-yawAmt);
      const sn = Math.sin(-yawAmt);
      x = ox + dx * cs - dy * sn;
      y = oy + dx * sn + dy * cs;
    }
    const { q, r } = pixelToAxial(x, y, lastFrame.hexSize);
    const key = cellKey(q, r);
    return lastFrame.map.cells.has(key) ? key : null;
  }

  function resetCamera(): void {
    cam = defaultCam();
    if (lastFrame) draw(lastFrame);
  }

  function bindInput(onSelect: (key: string) => void): void {
    onSelectCb = onSelect;
    const el = surface();
    el.style.pointerEvents = "auto";
    el.style.cursor = lastFrame?.mapCamera === false ? "pointer" : "grab";
    if (inputBound) return;
    inputBound = true;

    const redraw = () => {
      if (lastFrame) draw(lastFrame);
    };

    el.addEventListener(
      "wheel",
      (ev: WheelEvent) => {
        if (destroyed || !lastFrame) return;
        if (lastFrame.mapCamera === false) return;
        ev.preventDefault();
        const reduce = lastFrame.reduceMotion || prefersReducedMotion();
        if (reduce && Math.abs(ev.deltaY) < 1) return;
        const rect = el.getBoundingClientRect();
        const nx = (ev.clientX - rect.left) / Math.max(1, rect.width);
        const ny = (ev.clientY - rect.top) / Math.max(1, rect.height);
        const beforeX = viewBox.minX + nx * (viewBox.maxX - viewBox.minX);
        const beforeY = viewBox.minY + ny * (viewBox.maxY - viewBox.minY);
        const factor = ev.deltaY > 0 ? 0.9 : 1.1;
        cam.zoom = Math.max(0.55, Math.min(2.8, cam.zoom * factor));
        applyCamera();
        const afterX = viewBox.minX + nx * (viewBox.maxX - viewBox.minX);
        const afterY = viewBox.minY + ny * (viewBox.maxY - viewBox.minY);
        cam.panX += beforeX - afterX;
        cam.panY += beforeY - afterY;
        // Light tilt eases with zoom-in for depth cue (skipped under reduceMotion)
        // clampTilt keeps us below edge-on — never flip underside
        if (!reduce) {
          cam.tilt = clampTilt(0.04 + (cam.zoom - 1) * 0.035);
        } else {
          cam.tilt = 0;
        }
        redraw();
      },
      { passive: false },
    );

    el.addEventListener("contextmenu", (ev: MouseEvent) => {
      if (lastFrame?.mapCamera !== false) ev.preventDefault();
    });

    el.addEventListener("pointerdown", (ev: PointerEvent) => {
      if (destroyed) return;
      if (ev.button !== 0 && ev.button !== 2) return;
      if (lastFrame?.mapCamera === false && ev.button !== 0) return;
      el.setPointerCapture(ev.pointerId);
      const orbit =
        lastFrame?.mapCamera !== false &&
        (ev.button === 2 || (ev.button === 0 && ev.shiftKey));
      drag = {
        active: true,
        moved: false,
        mode: orbit ? "orbit" : "pan",
        startX: ev.clientX,
        startY: ev.clientY,
        origPanX: cam.panX,
        origPanY: cam.panY,
        origYaw: cam.yaw,
        origTilt: cam.tilt,
        pointerId: ev.pointerId,
      };
      if (lastFrame?.mapCamera !== false) {
        el.style.cursor = orbit ? "move" : "grabbing";
      }
    });

    el.addEventListener("pointermove", (ev: PointerEvent) => {
      if (!drag || !drag.active || drag.pointerId !== ev.pointerId || !lastFrame) return;
      if (lastFrame.mapCamera === false) return;
      const dx = ev.clientX - drag.startX;
      const dy = ev.clientY - drag.startY;
      if (!drag.moved && dx * dx + dy * dy > 16) drag.moved = true;
      if (!drag.moved) return;
      const rect = el.getBoundingClientRect();
      if (drag.mode === "orbit") {
        // Horizontal → yaw; vertical → tilt (clamped, no underside)
        cam.yaw = clampYaw(drag.origYaw + (dx / Math.max(1, rect.width)) * Math.PI * 0.9);
        cam.tilt = clampTilt(drag.origTilt - (dy / Math.max(1, rect.height)) * 0.18);
      } else {
        const worldW = viewBox.maxX - viewBox.minX;
        const worldH = viewBox.maxY - viewBox.minY;
        cam.panX = drag.origPanX - (dx / Math.max(1, rect.width)) * worldW;
        cam.panY = drag.origPanY - (dy / Math.max(1, rect.height)) * worldH;
      }
      redraw();
    });

    const endDrag = (ev: PointerEvent) => {
      if (!drag || drag.pointerId !== ev.pointerId) return;
      const wasClick = drag.active && !drag.moved;
      drag.active = false;
      el.style.cursor = lastFrame?.mapCamera === false ? "pointer" : "grab";
      try {
        el.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      if (wasClick && onSelectCb) {
        const key = pick(ev.clientX, ev.clientY);
        if (key) onSelectCb(key);
      }
      drag = null;
    };

    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);

    // Double-click / dbltap → reset camera (Light C1 only)
    el.addEventListener("dblclick", (ev: MouseEvent) => {
      if (!lastFrame || lastFrame.mapCamera === false) return;
      ev.preventDefault();
      resetCamera();
    });
  }

  function destroy(): void {
    destroyed = true;
    ro?.disconnect();
    ro = null;
    onSelectCb = null;
    const g = gl;
    if (g) {
      if (buf) g.deleteBuffer(buf);
      if (prog) g.deleteProgram(prog);
      if (isWebgl2 && vao) (g as WebGL2RenderingContext).deleteVertexArray(vao);
    }
    gl = null;
    prog = null;
    buf = null;
    vao = null;
    detach();
  }

  return {
    get canvas() {
      return surface();
    },
    get chrome() {
      return chrome;
    },
    glOk,
    mount,
    detach,
    draw,
    resize,
    pick,
    bindInput,
    resetCamera,
    destroy,
  };
}

/** Module-level singleton so full DOM rebuilds keep one GL context. */
let sharedBoard: WebglBoardHandle | null = null;

export function getSharedWebglBoard(): WebglBoardHandle {
  if (!sharedBoard) sharedBoard = createWebglBoard();
  return sharedBoard;
}

export function resetSharedWebglBoard(): void {
  sharedBoard?.destroy();
  sharedBoard = null;
}
